import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import RequireStore from "@/components/RequireStore";
import { toast } from "sonner";
import { formatCurrency, formatDate, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, CheckCircle2, Loader2, ArrowRight } from "lucide-react";

function MoneyField({ label, value, onChange, testId, big }) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-muted-foreground">€</span>
        <Input
          type="number"
          inputMode="decimal"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`pl-8 tabular-nums ${big ? "h-14 text-lg font-semibold" : "h-12"}`}
          placeholder="0,00"
          data-testid={testId}
        />
      </div>
    </div>
  );
}

export default function NuovoGiorno() {
  const navigate = useNavigate();
  const { storeId, currentStore } = useStore();
  const [date, setDate] = useState(todayISO());
  const [incasso, setIncasso] = useState("");
  const [deposits, setDeposits] = useState([{ amount: "", riferimento: "" }]);
  const [ricariche, setRicariche] = useState("");
  const [bar, setBar] = useState("");
  const [shop, setShop] = useState("");
  const [riconciliazione, setRiconciliazione] = useState("");
  const [riconMotivo, setRiconMotivo] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
  const totVersato = deposits.reduce((s, d) => s + num(d.amount), 0);

  const addDeposit = () => setDeposits([...deposits, { amount: "", riferimento: "" }]);
  const removeDeposit = (i) => setDeposits(deposits.filter((_, idx) => idx !== i));
  const updateDeposit = (i, field, val) =>
    setDeposits(deposits.map((d, idx) => (idx === i ? { ...d, [field]: val } : d)));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        date,
        store_id: storeId,
        incasso_contanti: num(incasso),
        ricariche: num(ricariche),
        bar: num(bar),
        shop: num(shop),
        note,
        deposits: deposits
          .filter((d) => num(d.amount) > 0)
          .map((d) => ({ amount: num(d.amount), riferimento: d.riferimento, note: "" })),
        riconciliazione: riconciliazione ? num(riconciliazione) : null,
        riconciliazione_motivazione: riconMotivo,
      };
      const { data } = await api.post("/daily-records", payload);
      setResult(data);
      toast.success("Giornata salvata correttamente");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setResult(null);
    setDate(todayISO());
    setIncasso(""); setDeposits([{ amount: "", riferimento: "" }]);
    setRicariche(""); setBar(""); setShop(""); setRiconciliazione(""); setRiconMotivo(""); setNote("");
  };

  if (result) {
    return (
      <div className="mx-auto max-w-lg" data-testid="nuovo-giorno-result">
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center sm:p-8">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
          <h1 className="mt-3 font-display text-2xl font-black">Giornata salvata!</h1>
          <p className="text-sm text-muted-foreground">{formatDate(result.date)}</p>
          <div className="mt-6 space-y-1 rounded-xl border border-border bg-card p-5 text-left font-mono text-sm">
            <RowR label="Incasso" value={result.incasso} />
            <RowR label="Versato" value={result.versato} />
            <RowR label="Saldo precedente" value={result.saldo_precedente} />
            {result.riconciliazione !== 0 && <RowR label="Riconciliazione" value={result.riconciliazione} />}
            <div className="my-2 border-t border-border" />
            <div className="flex items-center justify-between rounded-lg bg-primary/10 px-3 py-3">
              <span className="font-display font-bold uppercase">Nuovo saldo da versare</span>
              <span className="font-display text-xl font-black text-primary" data-testid="result-nuovo-saldo">{formatCurrency(result.nuovo_saldo)}</span>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button className="h-12 flex-1 font-bold" onClick={resetForm} data-testid="add-another-btn">
              <Plus className="mr-1 h-5 w-5" /> Nuova giornata
            </Button>
            <Button variant="secondary" className="h-12 flex-1 gap-2" onClick={() => navigate("/registro")}>
              Vai al Registro <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RequireStore>
    <div className="mx-auto max-w-2xl" data-testid="nuovo-giorno-page">
      <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Nuovo Giorno</h1>
      <div className="mb-4 mt-1 flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm" data-testid="ng-store-banner">
        <span className="text-muted-foreground">Stai inserendo i dati per:</span>
        <span className="font-bold text-primary">{currentStore?.name}</span>
      </div>
      <p className="mb-6 text-sm text-muted-foreground">I calcoli sono automatici.</p>

      <form onSubmit={submit} className="space-y-5">
        <div className="rounded-xl border border-border bg-card p-5">
          <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Data</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-12" required data-testid="ng-date-input" />
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <MoneyField label="Totale incasso contanti" value={incasso} onChange={setIncasso} testId="ng-incasso-input" big />
        </div>

        {/* Versamenti dinamici */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Versamenti</Label>
            <span className="text-sm text-muted-foreground">Totale versato: <b className="tabular-nums text-[#3B9BFF]">{formatCurrency(totVersato)}</b></span>
          </div>
          <div className="space-y-3">
            {deposits.map((d, i) => (
              <div key={i} className="flex items-end gap-2" data-testid={`ng-deposit-row-${i}`}>
                <div className="flex-1">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-semibold text-muted-foreground">€</span>
                    <Input type="number" inputMode="decimal" step="0.01" value={d.amount}
                      onChange={(e) => updateDeposit(i, "amount", e.target.value)}
                      className="h-12 pl-8 tabular-nums" placeholder={`Versamento ${i + 1}`}
                      data-testid={`ng-deposit-amount-${i}`} />
                  </div>
                </div>
                <div className="flex-1">
                  <Input value={d.riferimento} onChange={(e) => updateDeposit(i, "riferimento", e.target.value)}
                    className="h-12" placeholder="Riferimento (opz.)" data-testid={`ng-deposit-ref-${i}`} />
                </div>
                {deposits.length > 1 && (
                  <Button type="button" variant="ghost" size="icon" className="h-12 w-12 text-destructive"
                    onClick={() => removeDeposit(i)} data-testid={`ng-deposit-remove-${i}`}>
                    <Trash2 className="h-5 w-5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button type="button" variant="secondary" className="mt-3 gap-2" onClick={addDeposit} data-testid="ng-add-deposit-btn">
            <Plus className="h-4 w-4" /> Aggiungi versamento
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 rounded-xl border border-border bg-card p-5 sm:grid-cols-3">
          <MoneyField label="Ricariche" value={ricariche} onChange={setRicariche} testId="ng-ricariche-input" />
          <MoneyField label="Bar" value={bar} onChange={setBar} testId="ng-bar-input" />
          <MoneyField label="Shop" value={shop} onChange={setShop} testId="ng-shop-input" />
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <MoneyField label="Riconciliazione (+/−, opzionale)" value={riconciliazione} onChange={setRiconciliazione} testId="ng-riconciliazione-input" />
          {riconciliazione !== "" && riconciliazione !== "0" && (
            <div className="mt-3">
              <Input value={riconMotivo} onChange={(e) => setRiconMotivo(e.target.value)} className="h-11" placeholder="Motivazione riconciliazione" data-testid="ng-ricon-motivo-input" />
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Note</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (opzionale)" data-testid="ng-note-input" />
        </div>

        <Button type="submit" disabled={loading} className="h-14 w-full text-base font-bold" data-testid="ng-submit-btn">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="mr-2 h-5 w-5" /> Salva Giornata</>}
        </Button>
      </form>
    </div>
    </RequireStore>
  );
}

function RowR({ label, value }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{formatCurrency(value)}</span>
    </div>
  );
}
